import type { ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import { cn } from "@/utils/helpers";
import { kitButtonClass } from "../../components/kit/Button";

/** Button / Primary and Button / Secondary: 32px capsules, 13/500, 14 in. */
export const statePrimary = kitButtonClass({ variant: "primary" });
export const stateSecondary = kitButtonClass({ variant: "secondary" });
/** Text action: accent ink, no box. */
export const stateLink =
  "inline-flex h-8 items-center px-1 text-[13px] font-medium text-co-accent-ink outline-none hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent";

/**
 * A calendar state as a centred card over the empty grid (library Dialog /
 * Confirm, 560 wide, radius 16, floating chrome): not planned yet, no approved
 * strategy, planning failed.
 */
export function CentreCard({
  title,
  body,
  detail,
  actions,
  alert = false,
}: {
  title: string;
  body: ReactNode;
  detail?: ReactNode;
  actions?: ReactNode;
  alert?: boolean;
}) {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center p-6">
      <section
        role="region"
        aria-label={title}
        className="co-glass-chrome pointer-events-auto relative flex w-[560px] max-w-full flex-col rounded-panel"
      >
        <div className="flex flex-col gap-1.5 px-6 pb-2 pt-6">
          <h2 className="flex items-center gap-2.5 font-section text-[20px] font-extrabold leading-[1.1] tracking-[0.3px] text-co-text">
            {alert ? <AlertCircle className="size-[17px] shrink-0 text-co-alert-ink" aria-hidden="true" /> : null}
            {title}
          </h2>
          <div className="text-[13px] leading-[1.55] text-co-muted">{body}</div>
        </div>
        {detail ? <div className="flex flex-col gap-2 px-6 py-5">{detail}</div> : null}
        {actions ? <div className="flex flex-wrap items-center justify-end gap-3 px-6 pb-6 pt-4">{actions}</div> : null}
      </section>
    </div>
  );
}

/** A centred message with no card: the empty filter, a failed read. */
export function CentreMessage({
  title,
  body,
  actions,
  className,
}: {
  title: string;
  body?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("grid min-h-[420px] place-items-center px-6 py-16 text-center", className)}>
      <div className="flex max-w-[520px] flex-col items-center gap-3">
        <h2 className="font-section text-[24px] font-bold leading-tight text-co-text">{title}</h2>
        {body ? <p className="text-[15px] leading-relaxed text-co-text2">{body}</p> : null}
        {actions ? <div className="flex flex-wrap items-center justify-center gap-3 pt-2">{actions}</div> : null}
      </div>
    </div>
  );
}

/** Calendar / Post card · Loading: three bars in a month card's place while the month loads. */
export function CardSkeleton() {
  return (
    <span aria-hidden="true" className="flex flex-col gap-2 rounded-card border border-co-line bg-co-surface px-2.5 pb-3 pt-2.5">
      <span className="h-2.5 w-full rounded-tag bg-co-surface-solid motion-safe:animate-pulse" />
      <span className="h-2.5 w-[90px] rounded-tag bg-co-surface-solid motion-safe:animate-pulse" />
      <span className="h-2 w-[60px] rounded-tag bg-co-surface-solid motion-safe:animate-pulse" />
    </span>
  );
}

/** The scope a plan will fill, as Calendar / Scope draws it inside a state card. */
export function ScopeLine({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-[18px] gap-y-1 border-t border-co-line px-8 pb-3.5 pt-3 text-[13px] leading-[18px]">
      <span className="font-semibold text-co-text">{title}</span>
      {items.map((item) => (
        <span key={item} className="text-co-text2">
          {item}
        </span>
      ))}
    </div>
  );
}
